import React, { useContext, useState } from "react"
import {
  AllVehicleExpandedSelectorModuleProps,
  trim,
} from "./AllVehicleExpandedSelectorModuleProps"
import { LanguageContext } from "../../../../contexts/Language"
import tw from "twin.macro"
import { TabProps, Tabs } from "../../../atoms/Tabs"
import useDrag from "../../../../hooks/useDrag"
import TrimsCarousel from "./TrimsCarousel"
import { Switch } from "../../../atoms/Switch"
import { ButtonLink } from "../../../atoms/Button"
import { Family } from "../../../../global"
import {
  generateInventoryLink,
  generateModelCompareLink,
} from "../../../../helpers"
import useTealiumEvent from "../../../../hooks/Tealium/useTealiumEvent"
import { get } from "http"

const AllVehicleExpandedSelectorModule: React.FC<
  AllVehicleExpandedSelectorModuleProps
> = ({ family, ctaType, title = null }) => {
  const { _ } = useContext(LanguageContext)
  const [activeTab, setActiveTab] = useState(0)
  const { trackTealEvent } = useTealiumEvent()

  let categories: string[] = []

  switch (family?.name) {
    case "Cars & Minivans":
      categories = ["All", "Hybrid", "AWD", "XSeries", "Plug-In Hybrid EV"]
      break
    case "Crossovers & SUVs":
      categories = ["All", "Hybrid", "AWD", "XSeries", "Plug-In Hybrid EV"]
      break
    case "Trucks":
      categories = ["All", "Hybrid", "4WD", "XSeries"]
      break
    case "Vehicles Starting Under $30,000":
      categories = ["All", "Cars", "Hybrids", "Crossovers & SUVs"]
      break
    default:
      categories = ["All"]
  }

  const extractDataAndTrims = (family: Family) => {
    return family.routes.map(route => {
      const slug = route?.route?.slug?.current

      const trims = route?.route?.page?.vehicle?.models?.flatMap(modelGroup => {
        const modelsData =
          modelGroup?._type === "vehicleModelGroup"
            ? modelGroup.models?.flatMap(mo => mo.model)
            : modelGroup?._type === "vehicleModel"
              ? [modelGroup.model]
              : []
        return modelsData
          ?.map(model => {
            const category = categories[activeTab]
            if (category === "All") {
              return model
            } else if (
              (category === "Hybrid" || category == "Hybrids") &&
              model?.fuelType === "Hybrid"
            ) {
              return model
            } else if (
              category === "AWD" &&
              model?.characteristics?.driveTrain === "AWD"
            ) {
              return model
            } else if (
              category === "4WD" &&
              model?.characteristics?.driveTrain === "4WD"
            ) {
              return model
            } else if (category === "XSeries" && model?._type === "customModel")
              return model
            else if (
              category === "Plug-In Hybrid EV" &&
              model?.fuelType === "Plug-in Hybrid"
            )
              return model
            else if (
              category === "Cars" &&
              (model?.series?.category === "Cars & Minivan" ||
                model?.series?.name === "Prius")
            )
              return model
            else if (
              category === "Crossovers & SUVs" &&
              model?.series?.category === "Crossovers & SUVs"
            )
              return model
            else return null
          })
          .filter(model => model !== null)
      })

      const seriesName = route?.route?.page?.vehicle?.series?.name

      return { slug, seriesName, trims }
    })
  }

  const modelData = extractDataAndTrims(family)

  const getInventoryCTA = (series: string, familyName: string) => {
    let link = null
    if (familyName === "Vehicles Starting Under $30,000") {
      link = `${generateInventoryLink({
        series: series,
        priceMin: 20000,
        priceMax: 30000,
      })}&isAffordable=true`
    } else {
      link = generateInventoryLink({
        series: series,
      })
    }
    return link
  }

  return (
    <section css={[tw`py-20`]}>
      <h2
        css={[tw`text-3xl block leading-[104.6%] text-center mb-6 lg:text-5xl`]}
      >
        {title ?? _(`All ${family?.name}`)}
      </h2>
      <div>
        <Tabs
          activeFamily={activeTab}
          families={categories}
          setActiveFamily={setActiveTab}
          css={[tw`block bg-none text-xl`]}
          centerTabs={true}
        >
          {categories?.map((category, index) => {
            return (
              <Tab title={category} key={index}>
                {modelData &&
                  modelData.map(model => {
                    const [showCompare, setShowCompare] = useState(false)
                    const [selectedTrims, setSelectedTrims] = useState<trim[]>(
                      [],
                    )
                    if (model?.trims?.length == 0) {
                      return null
                    }
                    const trimsToCompare: {
                      name: string
                      year: string | number | undefined
                    }[] = selectedTrims.map(t => {
                      return {
                        name: t.name,
                        year: t.year,
                      }
                    })
                    const compareLink: string = generateModelCompareLink(
                      model?.slug,
                      trimsToCompare,
                    )
                    return (
                      <div
                        key={`${model.slug}-${index}-${category}`}
                        css={[
                          tw`flex flex-col gap-10 max-w-[1440px] w-full mx-auto `,
                        ]}
                      >
                        <div
                          css={[
                            tw`grid grid-cols-1 lg:grid-cols-3 content-stretch`,
                          ]}
                        >
                          <div
                            css={[
                              tw`col-span-1 flex gap-5 justify-between px-5 lg:(justify-start px-0)`,
                            ]}
                          >
                            <p css={[tw`text-2xl`]}>{model.seriesName}</p>
                            {category !== "XSeries" && (
                              <Switch
                                label={"Compare"}
                                checked={showCompare}
                                onChange={() => {
                                  showCompare && setSelectedTrims([])
                                  setShowCompare(!showCompare)
                                }}
                                css={[
                                  tw`gap-2 uppercase font-semibold shadow-none`,
                                ]}
                              />
                            )}
                          </div>
                          <div css={[tw`col-span-1`]}>
                            {showCompare && (
                              <p
                                css={[
                                  tw`text-center w-full font-semibold text-sm mx-auto inline-block align-bottom`,
                                ]}
                              >
                                {_(
                                  "Please select up to three models to compare.",
                                )}{" "}
                                <span css={[tw`text-toyotaRed`]}>
                                  {" "}
                                  {`${selectedTrims.length}/3`}
                                </span>
                              </p>
                            )}
                          </div>
                        </div>
                        <TrimsCarousel
                          model={model.seriesName!}
                          trims={model.trims as trim[] | [] | undefined}
                          showCompare={showCompare}
                          setSelectedTrims={setSelectedTrims}
                          selectedTrims={selectedTrims}
                        />
                        <div
                          css={[
                            tw`flex flex-col px-5 gap-5 justify-center lg:(flex-row px-0) `,
                          ]}
                        >
                          {showCompare && (
                            <ButtonLink
                              primary
                              forceExternal={true}
                              to={compareLink ? compareLink : ""}
                              disabled={selectedTrims.length < 2}
                            >
                              {_("Compare Now")}
                            </ButtonLink>
                          )}
                          {ctaType == "seriesInventoryPage" ? (
                            <ButtonLink
                              secondary
                              to={getInventoryCTA(
                                model?.seriesName!,
                                family?.name,
                              )}
                              analytics-id={`explore series:all segment:`}
                              onClick={() =>
                                trackTealEvent({
                                  tealium_event: "cta_click",
                                  link_href: model?.slug,
                                  coupon_module_text: ` ${_("View")} ${model.seriesName} ${_("Inventory")} ${_("Under $30,000")}`,
                                })
                              }
                            >
                              {_("View")} {model.seriesName} {_("Inventory")}{" "}
                              {family.name ==
                                "Vehicles Starting Under $30,000" &&
                                _("Under $30,000")}
                            </ButtonLink>
                          ) : (
                            <ButtonLink
                              secondary
                              to={model?.slug}
                              analytics-id={`explore series:all segment:`}
                              onClick={() =>
                                trackTealEvent({
                                  tealium_event: "cta_click",
                                  link_href: model?.slug,
                                  coupon_module_text: `${_("Explore More")} ${model.seriesName}`,
                                })
                              }
                            >
                              {_("Explore More")} {model.seriesName}
                            </ButtonLink>
                          )}
                        </div>
                      </div>
                    )
                  })}
              </Tab>
            )
          })}
        </Tabs>
      </div>
    </section>
  )
}

export default AllVehicleExpandedSelectorModule

/* TAB CONTENT */
const Tab: React.FC<TabProps> = ({ children }) => {
  const { handleMouseUp } = useDrag()

  return (
    <div onMouseUp={handleMouseUp} css={[tw`mt-10`]}>
      <div css={[tw`flex flex-col gap-10 h-full w-full`]}>{children}</div>
    </div>
  )
}
